import { z } from "zod";

/**
 * Replies that waited while their campaign was being set up (Dawid, about 10:24 CEST on
 * 2026-09-29): no age limit, and they are drafted with their age in mind. Past a few working days
 * the draft owns the delay in one host-written sentence; past about two weeks it is a short
 * check-in rather than a full answer. Both thresholds are workspace settings. A reply released from a
 * drafting switch, or found days late on a conversation a run read, is drafted the same way (Dawid,
 * decision 8d484c62, 2026-10-14).
 *
 * Pure: the clock and the lead's received time are inputs, so the same decision replays.
 */

/** Why the app held the reply before drafting it. Each value names the release that ended the hold. */
export const SetupHoldCauseSchema = z.enum([
  /** The copy read passed and released a campaign held until its copy was read. */
  "copy_check",
  /** A rep released a campaign before its copy had been read. */
  "released_before_copy_read",
  /** The campaign's setup went live: a confirmed setup, an accepted preview or an operator move. */
  "campaign_setup",
]);
export type SetupHoldCause = z.infer<typeof SetupHoldCauseSchema>;

/**
 * Why a reply reached drafting late. A setup hold names its release; two more paths wait as long
 * (Dawid, decision 8d484c62, 2026-10-14): a reply held while a rep had drafting switched off and
 * released when it was switched back on, and the lead's next message on a conversation a run read,
 * found more than a day late by reconciliation (docs/2026-10-14-late-reply-drafts.md).
 */
export const LateReplyCauseSchema = z.enum([
  ...SetupHoldCauseSchema.options,
  /** Held while a drafting switch was off, released when it was switched back on. */
  "drafting_off",
  /** Found more than a day after it arrived, on a conversation a run read. */
  "found_late",
]);
export type LateReplyCause = z.infer<typeof LateReplyCauseSchema>;

/**
 * Whether a submission's policy names a cause the build before refused as an invalid submission.
 * Such a refusal recorded no command, so the workflow asks a patch before admitting one, and a
 * refusal recorded by that build replays as a refusal (docs/2026-10-14-slow-reply-line.md).
 */
export function causeAddedForWaitedReplies(
  policy: { heldBy: LateReplyCause } | undefined,
): boolean {
  return policy?.heldBy === "drafting_off" || policy?.heldBy === "found_late";
}

const DEFAULT_DELAY_LINE_AFTER_WORKING_DAYS = 2;
const DEFAULT_CHECK_IN_AFTER_DAYS = 14;

export const LateReplyThresholdsSchema = z
  .strictObject({
    delayLineAfterWorkingDays: z.number().int().min(1).max(30),
    checkInAfterDays: z.number().int().min(2).max(90),
  })
  .refine((value) => value.checkInAfterDays > value.delayLineAfterWorkingDays, {
    message: "The check-in threshold must be later than the delay-line threshold",
    path: ["checkInAfterDays"],
  });
export type LateReplyThresholds = z.infer<typeof LateReplyThresholdsSchema>;

export const DEFAULT_LATE_REPLY_THRESHOLDS: LateReplyThresholds = {
  delayLineAfterWorkingDays: DEFAULT_DELAY_LINE_AFTER_WORKING_DAYS,
  checkInAfterDays: DEFAULT_CHECK_IN_AFTER_DAYS,
};

/**
 * What a drafting run is told about a reply that waited: why, and the workspace's thresholds. A
 * build before `drafting_off` and `found_late` refuses a submission carrying either
 * (docs/2026-10-07-rollback-floors.md).
 */
export const LateReplyPolicySchema = z.strictObject({
  heldBy: LateReplyCauseSchema,
  delayLineAfterWorkingDays: z.number().int().min(1).max(30),
  checkInAfterDays: z.number().int().min(2).max(90),
});
export type LateReplyPolicy = z.infer<typeof LateReplyPolicySchema>;

/**
 * The one sentence a late draft uses to own the delay. Host-owned, like the rep's follow-up
 * promise: the writer copies it, the judges may not rewrite it, and it never says why.
 */
export const DELAY_LINE = "Sorry for the slow reply.";

const DAY_MS = 86_400_000;

function utcDay(instant: number): number {
  return Math.floor(instant / DAY_MS);
}

/** Monday to Friday days after the day `from` falls on, up to and including the day of `to`, in UTC. */
export function workingDaysBetween(from: string, to: string): number {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  let count = 0;
  for (let day = utcDay(start) + 1; day <= utcDay(end); day++) {
    // 1970-01-01 was a Thursday, so day 0 is weekday 4.
    const weekday = (day + 4) % 7;
    if (weekday !== 0 && weekday !== 6) count++;
  }
  return count;
}

/** Whole calendar days from `from` to `to`. */
export function daysBetween(from: string, to: string): number {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.floor((end - start) / DAY_MS);
}

export type LateReplyStage = "on_time" | "delay_line" | "check_in";

/** How late a held reply is at the drafting clock, measured from the lead's own message. */
export function lateReplyStage(
  policy: Pick<LateReplyPolicy, "delayLineAfterWorkingDays" | "checkInAfterDays">,
  receivedAt: string,
  clock: string,
): LateReplyStage {
  if (daysBetween(receivedAt, clock) >= policy.checkInAfterDays) return "check_in";
  if (workingDaysBetween(receivedAt, clock) >= policy.delayLineAfterWorkingDays)
    return "delay_line";
  return "on_time";
}

/* ------------------------------------------------------------------------------------------------
 * Times and dates that have already passed.
 *
 * A reply drafted days after the lead wrote can meet "Thursday at 3pm works" when that Thursday is
 * gone. The detector resolves the lead's own day references against the day they wrote, and the
 * draft's absolute dates against the clock, at whole-day resolution and only once the day is over
 * in every zone (UTC+14). A time passed earlier the same day is therefore not caught; the replies
 * this exists for are days old.
 * --------------------------------------------------------------------------------------------- */

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
] as const;
const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;
const WEEKDAY_NAMES: readonly string[] = WEEKDAYS;
const MONTH = String.raw`(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept|sep|oct|nov|dec)\.?`;
const ORDINAL = String.raw`(\d{1,2})(?:st|nd|rd|th)?`;
const dayMonth = new RegExp(String.raw`\b${ORDINAL}\s+(?:of\s+)?${MONTH}(?:,?\s+(\d{4}))?`, "gi");
const monthDay = new RegExp(String.raw`\b${MONTH}\s+${ORDINAL}\b(?:,?\s+(\d{4}))?`, "gi");
const weekdayPattern =
  /\b(next\s+|this\s+|on\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)s?\b/gi;
const relativeDay = /\b(today|tomorrow)\b/gi;
/** Everything after the end of a day, until the day is over everywhere. */
const LATEST_ZONE_MS = 14 * 3_600_000;

function monthIndex(name: string): number {
  const key = name.toLowerCase().replace(/\.$/, "");
  return MONTHS.findIndex((month) => month.startsWith(key.slice(0, 3)));
}

/** A day the text refers to, as a UTC calendar day, with the words that named it. */
export type DayReference = { quote: string; day: number; kind: "date" | "weekday" | "relative" };

/** Absolute dates in `text`. A date with no year takes the year of `anchor`, or the next year if that lands far behind it. */
function absoluteDates(text: string, anchor: string): DayReference[] {
  const anchorAt = Date.parse(anchor);
  if (!Number.isFinite(anchorAt)) return [];
  const anchorYear = new Date(anchorAt).getUTCFullYear();
  const found: DayReference[] = [];
  const push = (quote: string, dayOfMonth: number, month: number, year: string | undefined) => {
    if (month < 0 || dayOfMonth < 1 || dayOfMonth > 31) return;
    let fullYear = year ? Number(year) : anchorYear;
    let at = Date.UTC(fullYear, month, dayOfMonth);
    // "3 January" written in late December means the coming January.
    if (!year && at < anchorAt - 180 * DAY_MS) {
      fullYear += 1;
      at = Date.UTC(fullYear, month, dayOfMonth);
    }
    if (new Date(at).getUTCDate() !== dayOfMonth) return;
    found.push({ quote, day: utcDay(at), kind: "date" });
  };
  for (const match of text.matchAll(dayMonth))
    push(match[0], Number(match[1]), monthIndex(match[2] ?? ""), match[3]);
  for (const match of text.matchAll(monthDay))
    push(match[0], Number(match[2]), monthIndex(match[1] ?? ""), match[3]);
  return found;
}

/**
 * Weekday and today/tomorrow references in a message, resolved against the day it was written.
 * A bare weekday is its next occurrence on or after that day, "next" adds a week to be safe, so a
 * reference counts as passed only when even its latest reading is over.
 */
function relativeDays(text: string, writtenAt: string): DayReference[] {
  const at = Date.parse(writtenAt);
  if (!Number.isFinite(at)) return [];
  const written = utcDay(at);
  const writtenWeekday = (written + 4) % 7;
  const found: DayReference[] = [];
  for (const match of text.matchAll(weekdayPattern)) {
    const target = WEEKDAY_NAMES.indexOf((match[2] ?? "").toLowerCase());
    let offset = (target - writtenWeekday + 7) % 7;
    if (/^next/i.test(match[1] ?? "")) offset += 7;
    found.push({ quote: match[0].replace(/^on\s+/i, ""), day: written + offset, kind: "weekday" });
  }
  for (const match of text.matchAll(relativeDay))
    found.push({
      quote: match[0],
      day: written + (match[1]?.toLowerCase() === "tomorrow" ? 1 : 0),
      kind: "relative",
    });
  return found;
}

/** Whether the whole of a UTC calendar day is over in every zone at `clock`. */
function dayIsOver(day: number, clock: string): boolean {
  const now = Date.parse(clock);
  return Number.isFinite(now) && (day + 1) * DAY_MS + LATEST_ZONE_MS <= now;
}

/** Words that mark a sentence as speaking about the passed time, not confirming it. */
const pastMarker =
  /\b(?:passed|missed|gone|was|were|last|already|sorry|instead|another|other|new|different|reschedul\w*|since|ago|earlier|thanks?|thank you|wrote|mentioned|said|sent|received)\b/i;

function sentences(text: string): { text: string; start: number }[] {
  const found: { text: string; start: number }[] = [];
  const pattern = /[^.!?\n]+[.!?]?/g;
  for (const match of text.matchAll(pattern))
    if (match[0].trim()) found.push({ text: match[0], start: match.index });
  return found;
}

export type PassedTimeFinding = { quote: string; reason: "lead_proposed" | "date_passed" };

/**
 * Passed days the lead named in their own words, as the draft would have to avoid confirming them.
 * `leadMessages` are the lead-authored texts with the time each was received.
 */
export function passedLeadDays(
  leadMessages: readonly { text: string; receivedAt: string }[],
  clock: string,
): DayReference[] {
  return leadMessages.flatMap((message) =>
    [
      ...absoluteDates(message.text, message.receivedAt),
      ...relativeDays(message.text, message.receivedAt),
    ].filter((reference) => dayIsOver(reference.day, clock)),
  );
}

/**
 * Where the draft confirms a day that is over: a weekday the lead proposed that has passed, or any
 * absolute date that has passed, in a sentence that does not speak of it as past or replaced.
 */
export function passedTimesConfirmed(
  draft: string,
  passedLead: readonly DayReference[],
  clock: string,
): PassedTimeFinding[] {
  const findings: PassedTimeFinding[] = [];
  const passedWeekdays = new Set<string>(
    passedLead
      .filter((reference) => reference.kind === "weekday")
      .flatMap((reference) => WEEKDAYS[(reference.day + 4) % 7] ?? []),
  );
  const passedDays = new Set(passedLead.map((reference) => reference.day));
  const clockAt = Date.parse(clock);
  if (!Number.isFinite(clockAt)) return [];
  for (const sentence of sentences(draft)) {
    if (pastMarker.test(sentence.text)) continue;
    for (const reference of absoluteDates(sentence.text, clock)) {
      if (dayIsOver(reference.day, clock))
        findings.push({
          quote: reference.quote,
          reason: passedDays.has(reference.day) ? "lead_proposed" : "date_passed",
        });
    }
    for (const match of sentence.text.matchAll(weekdayPattern)) {
      const name = (match[2] ?? "").toLowerCase();
      // "next Thursday" in the draft is a new day, not the one the lead proposed.
      if (/^next/i.test(match[1] ?? "")) continue;
      // "Thursday 17 September" names its own date, checked as a date above.
      const after = sentence.text.slice((match.index ?? 0) + match[0].length);
      if (
        new RegExp(String.raw`^,?\s+(?:the\s+)?(?:\d{1,2}(?:st|nd|rd|th)?\b|${MONTH})`, "i").test(
          after,
        )
      )
        continue;
      if (passedWeekdays.has(name))
        findings.push({ quote: match[0].replace(/^on\s+/i, ""), reason: "lead_proposed" });
    }
  }
  return findings;
}
